import { Component, HostListener, Injector, Input, OnChanges, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GTMService } from '../../../shared/gtm.service';
import { ModalService } from '../../../shared/modal.service';
import { MODAL_DATA } from '../../../shared/tokens/modal-data.token';
import { PcReservationTrackEvents } from '../../../shared/types/pc-reservation-track-events.enum';
import { LiveClassPcPromoService } from '../../../shared/live-class-pc-promo.service';
import { BetaTesterService } from '../../../shared/beta-tester.service';
import { BetaFeatureType } from '../../../shared/types/beta-feature-type';
import { ProfileServiceConfigurationService } from '../../../shared/profile-service-configuration.service';
import { TranslationService } from '../../../shared/translation.service';
import { LiveClassDto } from '../../../shared/types/LiveClassDto';
import { GamificationActivityType } from '../../../shared/types/gamification-activity-type';
import { UtilsService } from '../../../shared/utils.service';
import { BREAKPOINT_MAP, Breakpoints, CARD_CONFIG, CardConfigDataType } from '../group-class/gc-card-setup';
import { LiveClassLessonTrack } from '../live-class-lesson-track-dto';
import { ImageCardConfig } from '../image-card-config/image-card-config';
import { PrivateClassThankYouModalComponent } from '../private-class-banner/private-class-thank-you-modal/private-class-thank-you-modal.component';
import { PrivateClassTrackService } from '../private-class-banner/private-class-track/private-class-track.service';
import { PcReservationIframeComponent } from '../pc-reservation-iframe/pc-reservation-iframe.component';
import { PcBannerIframeComponent, PrivateClassBannerCancelPayload } from '../pc-banner-iframe/pc-banner-iframe.component';
import { PcCancelModalComponent } from '../pc-cancel-modal/pc-cancel-modal.component';

@Component({
  selector: 'lp2-live-class-revamp-section',
  templateUrl: './live-class-revamp-section.component.html',
})
export class LiveClassRevampSectionComponent implements OnInit, OnChanges {
  @Input() liveClasses: Array<LiveClassDto> = [];
  @Input() activityPoints: Map<GamificationActivityType, number>;
  @Input() teacherTypeControl: FormControl;
  @Input() isSwitchVisible: boolean;

  public privateLiveClass: LiveClassDto;
  public groupLiveClasses: Array<LiveClassDto>;
  public isPcUpsellEligible: boolean;

  public bannerVm$: Observable<{
    pcReservationEnabled: boolean;
    privateClassTrack: LiveClassLessonTrack | null;
  }> = combineLatest([
    this.betaTesterService.isBetaTesterStudent(BetaFeatureType.PC_RESERVATION),
    this.profileServiceConfigurationService.isPCReservationEnabled$(),
  ]).pipe(
    map(([betaTesterEnabled, configEnabled]) => betaTesterEnabled || configEnabled),
    switchMap((pcReservationEnabled) =>
      this.privateClassTrackService.getPrivateClassTrack().pipe(
        map((privateClassTrack) => ({ pcReservationEnabled, privateClassTrack })),
        catchError(() => of({ pcReservationEnabled, privateClassTrack: null }))
      )
    )
  );
  public gcCardConfig: ImageCardConfig | null = null;
  private showCustomUpsellBanner = false;

  public get showUpsellBanner() {
    return (
      this.isPcUpsellEligible &&
      !this.privateLiveClass &&
      !this.showCustomUpsellBanner &&
      this.groupLiveClasses.length >= 2
    );
  }

  public get shouldDisplayLiveClassHeader() {
    return this.privateLiveClass || this.shouldDisplayGroupClasses;
  }

  public get shouldDisplayGroupClasses() {
    return this.groupLiveClasses && this.groupLiveClasses.length > 0;
  }

  constructor(
    private privateClassTrackService: PrivateClassTrackService,
    private liveClassPcPromoService: LiveClassPcPromoService,
    private betaTesterService: BetaTesterService,
    private profileServiceConfigurationService: ProfileServiceConfigurationService,
    private translationService: TranslationService,
    private modalService: ModalService,
    private gtmService: GTMService
  ) {}

  @HostListener('window:resize', ['$event'])
  public onWindowResize() {
    if (this.gcCardConfig) {
      this.gcCardConfig.setImageSize(this.getBreakpoint());
    }
  }

  ngOnInit() {
    this.isPcUpsellEligible = this.liveClassPcPromoService.isPcUpsellEligible();
    this.setGCCardConfig();
  }

  ngOnChanges() {
    if (this.liveClasses) {
      this.privateLiveClass = this.liveClasses.filter((liveClass) => liveClass.type === 'Private Class')[0];
      this.groupLiveClasses = this.liveClasses.filter((liveClass) => liveClass.type === 'Live Class');
    }
  }

  public get canChangeTeacher() {
    const enabledClasses = this.liveClasses.filter((l) => l.signupEnabled);
    const hasInvalidStatus = enabledClasses.some((l) => l.tileState === 'LOCKED');
    return !hasInvalidStatus;
  }

  public openReservationModal(banner?: PcBannerIframeComponent) {
    const modalRef = this.modalService.open(PcReservationIframeComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'pc-reservation-modal',
    });
    modalRef.componentInstance.completed.subscribe(() => {
      modalRef.close();
    });
    modalRef.componentInstance.closed.subscribe(() => {
      modalRef.close();
    });
    const refetchBanner = () => {
      if (banner) {
        banner.postRefetch();
      }
    };
    modalRef.result.then(refetchBanner, refetchBanner);
  }

  public openCancelModal(data: PrivateClassBannerCancelPayload, banner: PcBannerIframeComponent) {
    this.gtmService.sendGA4Event(PcReservationTrackEvents.CANCEL_BUTTON_CLICKED);
    const modalRef = this.modalService.open(PcCancelModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'pc-cancel-modal-window',
      injector: Injector.create({
        providers: [{ provide: MODAL_DATA, useValue: data }],
      }),
    });
    modalRef.result.then(
      () => banner.postRefetch(),
      () => undefined
    );
  }

  public handleContactMe() {
    this.liveClassPcPromoService.saveToCampaign();
    this.modalService.open(PrivateClassThankYouModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });
  }

  private setGCCardConfig() {
    const shouldDisplayCard = this.groupLiveClasses && this.groupLiveClasses.length < 2;
    if (!shouldDisplayCard) {
      return;
    }
    const cardConfig = new ImageCardConfig();
    const hasPrivateClass = !!this.privateLiveClass;
    this.profileServiceConfigurationService.liveClassPromoTileEnabled$().subscribe((promoFlag) => {
      let cardConfigData: CardConfigDataType;
      switch (true) {
        case promoFlag:
          cardConfigData = CARD_CONFIG.PROMO;
          break;
        case hasPrivateClass:
          cardConfigData = CARD_CONFIG.HAS_PC;
          break;
        case this.isPcUpsellEligible:
          this.showCustomUpsellBanner = true;
          cardConfigData = CARD_CONFIG.NO_PC.UPSELL_ELIGIBLE;
          cardConfig.setAction(this.handleContactMe.bind(this));
          break;
        default:
          cardConfigData = CARD_CONFIG.NO_PC.NOT_UPSELL_ELIGIBLE;
          break;
      }

      cardConfig.setLink(this.translationService.translate(cardConfigData.linkKey));
      cardConfig.setImageSrc(this.translationService.translate(cardConfigData.imageKey));
      cardConfig.setImageSize(this.getBreakpoint());

      if (!cardConfig.imageSrc) {
        this.showCustomUpsellBanner = false;
        return;
      }

      this.gcCardConfig = cardConfig;
    });
  }

  private getBreakpoint(): Breakpoints {
    return UtilsService.objectEntries(BREAKPOINT_MAP).reduce((acc, [key, value]: [Breakpoints, number]) => {
      if (window.innerWidth >= value) {
        return key;
      }
      return acc;
    }, Breakpoints.SMALL);
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/live-class-revamp/live-class-revamp-section/live-class-revamp-section.component.ts